import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api, formatError, setToken, clearToken, getToken, logDev } from "../api";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  // null = checking, false = not auth, object = user
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    // Skip auto-check during Google callback; AuthCallback will handle it
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    if (!getToken()) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      clearToken();
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password, expectedRole = null) => {
    try {
      const payload = { email, password };
      if (expectedRole) payload.expected_role = expectedRole;
      const { data } = await api.post("/auth/login", payload);
      if (data.access_token) setToken(data.access_token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) { return { ok: false, error: formatError(e) }; }
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      if (data.access_token) setToken(data.access_token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) { return { ok: false, error: formatError(e) }; }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); }
    catch (err) { logDev.warn("[auth] logout request failed:", err?.message || err); }
    clearToken();
    setUser(false);
  };

  // Stable context value — prevents every consumer from re-rendering on each
  // parent render (the Provider's child tree is huge).
  const ctxValue = useMemo(
    () => ({ user, setUser, loading, login, register, logout, refresh }),
    [user, loading, refresh],
  );

  return (
    <AuthCtx.Provider value={ctxValue}>
      {children}
    </AuthCtx.Provider>
  );
}
